/**
 * ProperFacets — progressive enhancement over the working <form>.
 *
 * The page already renders real results and applies real filters with zero
 * JS (see render.php). This only makes that same form feel instant: it is
 * never required, and every path below either enhances or falls back to the
 * plain GET submission the browser would have done anyway.
 *
 * Inert (adds zero listeners) if the page has no `[data-facets-form]`.
 */
( function () {
	if ( ! document.querySelector( '[data-facets-form]' ) ) { return; }

	var inflight = null;   // AbortController for the most recent request
	var debounce = null;   // timer id for the search box's debounced `input`

	// A set is EITHER one "all" wrapper (site-search: filters + results share
	// one container) OR a "filters" + a "results" wrapper (the four listing
	// pages) — never both shapes for the same set.
	function regionsFor( setId ) {
		var out = {};
		document.querySelectorAll( '.facets[data-facets="' + setId + '"]' ).forEach( function ( el ) {
			out[ el.dataset.facetsRegion ] = el;
		} );
		return out;
	}

	// A closed <details>'s content is non-interactive at the browser level —
	// gated on the real `open` attribute, not overridable by any CSS display
	// override of its children (measured live: a `display:block` override
	// alone left a checkbox on-screen but un-clickable/un-focusable). So
	// collapsing the panel on narrow viewports has to flip the real DOM
	// property, not just look closed. render.php now emits the panel OPEN by
	// default (fix round 1 / Fix 2) -- a no-JS visitor at ANY viewport sees it
	// open; this only ever CLOSES it, and only below 782px, so the mobile win
	// (first card visible without scrolling past the filters) survives.
	var panelMq = window.matchMedia( '(min-width: 782px)' );
	function syncPanels( scope ) {
		var open = panelMq.matches;
		( scope || document ).querySelectorAll( '.facets__panel' ).forEach( function ( el ) { el.open = open; } );
	}
	if ( panelMq.addEventListener ) { panelMq.addEventListener( 'change', function () { syncPanels( document ); } ); }
	else if ( panelMq.addListener ) { panelMq.addListener( function () { syncPanels( document ); } ); }

	// Re-attach the client-side term-filter input to every facet the server
	// marked `data-facets-filterable` (its value IS the placeholder text) —
	// including freshly swapped-in markup, which never carries one of its own.
	function injectFilters( scope ) {
		scope.querySelectorAll( '.facets__group[data-facets-filterable]' ).forEach( function ( group ) {
			if ( group.querySelector( '.facets__filter' ) ) { return; }
			var list = group.querySelector( '.facets__list' );
			if ( ! list ) { return; }
			var input = document.createElement( 'input' );
			input.type = 'text';
			input.className = 'facets__filter';
			input.placeholder = group.dataset.facetsFilterable;
			input.setAttribute( 'aria-label', group.dataset.facetsFilterable );
			list.before( input );
		} );
	}

	function apply( form ) {
		var root = form.closest( '.facets' );
		if ( ! root ) { form.submit(); return; }
		try {
			var setId = root.dataset.facets;
			var rs = regionsFor( setId );
			var busy = rs.all || rs.results;
			var params = new URLSearchParams( new FormData( form ) );
			params.set( 'facets_set', setId );
			var action = ( form.getAttribute( 'action' ) || location.pathname ).split( '?' )[ 0 ];

			if ( inflight ) { inflight.abort(); }
			inflight = new AbortController();
			if ( busy ) { busy.setAttribute( 'aria-busy', 'true' ); }

			fetch( action + '?' + params.toString(), {
				headers: { 'X-Facets': '1' }, signal: inflight.signal, credentials: 'same-origin',
			} )
				.then( function ( res ) { if ( ! res.ok ) { throw new Error( 'facets: ' + res.status ); } return res.json(); } )
				.then( function ( data ) {
					if ( rs.all ) {
						// `.facets__results` is part of the server's all() markup, so it
						// has to be re-created here or an AJAX update silently drops the
						// results container (measured: resultsDiv true before, false
						// after). data.results stays raw because the two-column
						// region="results" element IS the container in that shape.
						rs.all.innerHTML = data.filters + '<div class="facets__results">' + data.results + '</div>';
						injectFilters( rs.all );
						syncPanels( rs.all );
					} else {
						if ( rs.filters ) { rs.filters.innerHTML = data.filters; injectFilters( rs.filters ); syncPanels( rs.filters ); }
						if ( rs.results ) { rs.results.innerHTML = data.results; }
					}
					// Server-computed canonical URL: the SAME Url::canonical() a
					// no-JS submit's redirect check uses, so the two paths agree.
					history.pushState( { facets: setId }, '', data.url );
					var target = rs.all || rs.results;
					var summary = target && target.querySelector( '.facets__summary' );
					if ( summary ) { summary.focus(); }
					if ( busy ) { busy.setAttribute( 'aria-busy', 'false' ); }
				} )
				.catch( function ( err ) {
					if ( err.name === 'AbortError' ) { return; } // superseded, not a failure
					if ( busy ) { busy.setAttribute( 'aria-busy', 'false' ); }
					form.submit();
				} );
		} catch ( e ) {
			form.submit();
		}
	}

	document.addEventListener( 'change', function ( e ) {
		var form = e.target.closest( '[data-facets-form]' );
		if ( form ) { apply( form ); }
	} );

	document.addEventListener( 'submit', function ( e ) {
		var form = e.target.closest( '[data-facets-form]' );
		if ( ! form ) { return; }
		e.preventDefault();
		clearTimeout( debounce );
		apply( form );
	} );

	document.addEventListener( 'input', function ( e ) {
		var search = e.target.closest( '.facets__search-input' );
		if ( search ) {
			var form = search.closest( '[data-facets-form]' );
			if ( form ) { clearTimeout( debounce ); debounce = setTimeout( function () { apply( form ); }, 300 ); }
			return;
		}
		var filter = e.target.closest( '.facets__filter' );
		if ( ! filter ) { return; }
		var group = filter.closest( '.facets__group' );
		if ( ! group ) { return; }
		var more = group.querySelector( '.facets__more' );
		var showingExtra = more ? more.getAttribute( 'aria-expanded' ) === 'true' : true;
		var q = filter.value.trim().toLowerCase();
		group.querySelectorAll( '.facets__item' ).forEach( function ( li ) {
			if ( ! q ) { li.hidden = li.classList.contains( 'facets__item--extra' ) && ! showingExtra; return; }
			var term = li.querySelector( '.facets__term' );
			li.hidden = ( term ? term.textContent : '' ).toLowerCase().indexOf( q ) === -1;
		} );
	} );

	// facets__more: toggle the extra terms, keep aria-expanded honest.
	document.addEventListener( 'click', function ( e ) {
		var more = e.target.closest( '.facets__more' );
		if ( ! more ) { return; }
		var expanded = more.getAttribute( 'aria-expanded' ) === 'true';
		var group = more.closest( '.facets__group' );
		if ( group ) { group.querySelectorAll( '.facets__item--extra' ).forEach( function ( li ) { li.hidden = expanded; } ); }
		more.setAttribute( 'aria-expanded', String( ! expanded ) );
		// "Show more" becomes "Show less" while the extra terms are showing.
		var label = expanded ? more.getAttribute( 'data-label-more' ) : more.getAttribute( 'data-label-less' );
		if ( label ) { more.textContent = label; }
	} );

	// Back/Forward: reload so SSR markup is re-derived from the URL — the
	// simplest way to match what a fresh visit renders, no stale swap to redo.
	window.addEventListener( 'popstate', function () { location.reload(); } );

	injectFilters( document );
	syncPanels( document );
} )();
